"use client";

import { useState, type FormEvent } from "react";
import {
  Phone,
  Mail,
  User,
  MessageSquare,
  AlertTriangle,
  Shield,
  Ban,
  Send,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { SITE, SERVICES } from "@/lib/siteData";

type Urgency = "routine" | "urgent" | "emergency";

export default function ContactForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [urgency, setUrgency] = useState<Urgency>("routine");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      // Ya Ace Mailer — universal relay (Resend + server-side Privyr forward).
      // Returns real JSON {ok}, so success/failure is genuine (no more no-cors guessing).
      const res = await fetch("https://ya-ace-mailer.yossiasay1412.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site: "eagle-chimney",
          name,
          phone,
          email,
          service: service || "Not specified",
          urgency,
          message,
          source: typeof window !== "undefined" ? window.location.href : "",
          website: "", // honeypot
        }),
      });
      const data = (await res.json().catch(() => ({ ok: false }))) as { ok?: boolean };
      if (res.ok && data.ok) {
        setSubmitted(true);
      } else {
        setError("We couldn't send your request just now. Please call us directly.");
      }
    } catch {
      setError("Network error. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <CheckCircle className="h-12 w-12 text-[#1D7A2E] mx-auto mb-4" />
        <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">
          Message Sent!
        </h3>
        <p className="text-slate-700">
          We&apos;ll get back to you shortly. For immediate assistance, call{" "}
          <a
            href={SITE.phoneHref}
            className="text-[#7A5500] font-semibold underline"
          >
            {SITE.phone}
          </a>
        </p>
      </div>
    );
  }

  const urgencyOptions: { value: Urgency; label: string; desc: string }[] = [
    { value: "routine", label: "Routine", desc: "Within a few days" },
    { value: "urgent", label: "Urgent", desc: "Within 24 hours" },
    { value: "emergency", label: "Emergency", desc: "Need help NOW" },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8"
    >
      {/* Emergency Banner */}
      {urgency === "emergency" && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              Emergency? Call us directly for fastest response:
            </p>
            <a
              href={SITE.phoneHref}
              className="text-lg font-bold text-red-700 hover:underline"
            >
              {SITE.phone}
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Name */}
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-[#0A0A0A] mb-1.5"
          >
            Full Name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
            <input
              id="contact-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 text-[#0A0A0A] placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#E8BD4E]/40 focus:border-[#E8BD4E] min-h-[48px] text-sm"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="contact-phone"
            className="block text-sm font-medium text-[#0A0A0A] mb-1.5"
          >
            Phone Number *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
            <input
              id="contact-phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-5555"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 text-[#0A0A0A] placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#E8BD4E]/40 focus:border-[#E8BD4E] min-h-[48px] text-sm"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-[#0A0A0A] mb-1.5"
          >
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
            <input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 text-[#0A0A0A] placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#E8BD4E]/40 focus:border-[#E8BD4E] min-h-[48px] text-sm"
            />
          </div>
        </div>

        {/* Service Type */}
        <div>
          <label
            htmlFor="contact-service"
            className="block text-sm font-medium text-[#0A0A0A] mb-1.5"
          >
            Service Needed
          </label>
          <select
            id="contact-service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-slate-300 text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#E8BD4E]/40 focus:border-[#E8BD4E] min-h-[48px] text-sm bg-white"
          >
            <option value="">Select a service...</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Urgency Selector */}
      <div className="mt-5">
        <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
          How urgent is this?
        </label>
        <div className="grid grid-cols-3 gap-3">
          {urgencyOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setUrgency(opt.value)}
              className={`flex flex-col items-center py-3 px-2 rounded-xl border-2 transition-colors min-h-[48px] ${
                urgency === opt.value
                  ? opt.value === "emergency"
                    ? "border-red-500 bg-red-50"
                    : opt.value === "urgent"
                    ? "border-[#E8BD4E] bg-[#E8BD4E]/5"
                    : "border-[#B98002] bg-[#B98002]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <span
                className={`text-sm font-semibold ${
                  urgency === opt.value
                    ? opt.value === "emergency"
                      ? "text-red-700"
                      : opt.value === "urgent"
                      ? "text-[#7A5500]"
                      : "text-[#7A5500]"
                    : "text-[#0A0A0A]"
                }`}
              >
                {opt.label}
              </span>
              <span className="text-[11px] text-slate-700 mt-0.5">
                {opt.desc}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      <div className="mt-5">
        <label
          htmlFor="contact-message"
          className="block text-sm font-medium text-[#0A0A0A] mb-1.5"
        >
          Message
        </label>
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-600" />
          <textarea
            id="contact-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your chimney or fireplace issue..."
            rows={4}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 text-[#0A0A0A] placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#E8BD4E]/40 focus:border-[#E8BD4E] text-sm resize-none"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-4 text-sm text-red-600 font-medium">{error}</p>
      )}

      {/* Trust Micro-copy */}
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-700">
        <span className="flex items-center gap-1">
          <Shield className="h-3.5 w-3.5" />
          Free Estimate
        </span>
        <span className="flex items-center gap-1">
          <Ban className="h-3.5 w-3.5" />
          No Spam
        </span>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full flex items-center justify-center gap-2 bg-[#E8BD4E] hover:bg-[#B98002] disabled:bg-[#E8BD4E]/50 text-[#0A0A0A] font-bold py-4 rounded-full transition-colors min-h-[48px] text-base shadow-lg shadow-[#E8BD4E]/20"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            Send Message
          </>
        )}
      </button>
    </form>
  );
}
